import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';

import { createAdminTeamSchema } from '../schemas';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  useCreateTeamMutation,
  useOrganizationsShortQuery,
  usePartnerShortQuery,
  usePracticeShortQuery,
  useTeamNamesQuery,
} from '@common/cache';
import { paths } from '@common/types';
import { formatApiError } from '@common/errors/error-formatting.ts';

interface ICreateTeamFormShape {
  name: string;
  description?: string;
  record_id: string;
  record_type: 'organization' | 'partner' | 'practice';
  city?: string;
  state?: string;
}

const initialValues: ICreateTeamFormShape = {
  name: '',
  description: '',
  record_id: '',
  record_type: 'organization',
  city: '',
  state: '',
};

export const useCreateTeam = (handleOpenTeamDetails: (id: string) => void) => {
  const {
    control,
    handleSubmit,
    watch,
    formState: { isSubmitting, isDirty, isValid },
    reset,
    getValues,
  } = useForm<ICreateTeamFormShape>({
    mode: 'onChange',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver<ICreateTeamFormShape, any, any>(
      createAdminTeamSchema,
    ),
    defaultValues: initialValues,
  });

  const { mutateAsync, isPending } = useCreateTeamMutation();
  const [isOpen, setIsOpen] = useState(false);
  const [isSaveChangesModalOpen, setIsSaveChangesModalOpen] = useState(false);
  const { data: teamsShort = [] } = useTeamNamesQuery();

  const selectedRecordType = useWatch({ control, name: 'record_type' });

  const { data: organizationsShort = [] } = useOrganizationsShortQuery();
  const { data: partnersShort = [] } = usePartnerShortQuery();
  const { data: practicesShort = [] } = usePracticeShortQuery({});

  const recordsShort =
    (
      {
        partner: partnersShort,
        organization: organizationsShort,
        practice: practicesShort,
      } as const
    )[selectedRecordType] ?? organizationsShort;

  const recordTypes = [
    { value: 'organization', label: 'Organization' },
    { value: 'partner', label: 'Partner' },
    { value: 'practice', label: 'Practice' },
  ];

  const handleClose = () => {
    if (isDirty) {
      return setIsSaveChangesModalOpen(true);
    }
    setIsOpen(false);
    reset();
  };

  const handleOnClick = () => {
    setIsOpen(true);
  };

  const submitHandler = async (values: ICreateTeamFormShape) => {
    try {
      const response = await mutateAsync({
        name: values.name as paths['/team']['post']['requestBody']['content']['application/json']['name'],
        description: values.description,
        country: values.state,
        city: values.city,
        state: values.state,
        record_id: values.record_id,
      });

      if (response.data.id) {
        handleOpenTeamDetails(response.data.id);
      }

      setIsSaveChangesModalOpen(false);
      setIsOpen(false);
      reset();
      toast.success('You added a new user');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const onSubmit = handleSubmit(submitHandler);

  const handleCloseSaveChangesModal = () => {
    setIsSaveChangesModalOpen(false);
  };

  const handleDiscardChanges = () => {
    setIsSaveChangesModalOpen(false);
    setIsOpen(false);
    reset();
  };

  const handleSaveChanges = async () => {
    const values = getValues();

    try {
      await mutateAsync({
        name: values.name as paths['/team']['post']['requestBody']['content']['application/json']['name'],
        description: values.description,
        country: values.state,
        city: values.city,
        state: values.state,
        record_id: values.record_id,
      });

      setIsSaveChangesModalOpen(false);
      setIsOpen(false);
      reset();
      toast.success('You added a new user');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  return {
    control,
    onSubmit,
    isSubmitting,
    isOpen,
    submitHandler,
    handleSubmit,
    handleClose,
    handleOnClick,
    watch,
    teamsShort: teamsShort.map(o => ({ value: o, label: o })),
    recordsShort: recordsShort.map(o => ({ label: o.name, value: o.id })),
    recordTypes,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleSaveChanges,
    handleDiscardChanges,
    isValid,
    isEditSubmitting: isPending,
  };
};
